import React, { useEffect } from 'react'
import { useLocation, useMount } from 'react-use'
import { Link } from 'react-router'
import QrCode from 'qrcode.react'
import SVG from 'react-inlinesvg'

import { moment } from 'features/Moment'
import { Container, Row, Col } from 'components/Layout'
import Page from 'components/Page'
import PageHeader from 'components/Page/PageHeader'
import { Crumbs, Crumb } from 'components/BreadCrumbs'
import NumbersEdit from 'features/Numbers/NumbersEdit'
import CasesTimeline from 'features/Cases/CasesTimeline'
import Button from 'components/Button'
import InfoCard from 'components/Cards/InfoCard'
import StickyChannel from 'components/StickyChannel'
import SimpleTable from 'components/Tables/SimpleTable'
import { CallsTrendsVolumeContainer } from 'features/CallAnalytics/Cards/CallsTrendsVolume'
import IconPhone from 'components/Icons/Phone'
import IconCellMessage from 'components/Icons/CellMessage'
import Tooltip from 'components/Tooltip'

import cn from 'classnames'
import s from './NumbersEdit.scss'
import sText from 'components/Text/Text.scss'
import sLay from 'styles/modules/layout.scss'
import sMarg from 'styles/modules/margins.scss'

export default props => {
  useMount(() => {
    const { getNumber, getFlows, serviceId } = props
    getNumber(serviceId)
    getFlows()
  })

  const {
    service,
    showNumbersChangeUsagePlanModal,
    showNumbersCallPlanModal,
    showNumbersDisconnectModal,
    showCasesCreateTroubleTicket,
  } = props

  const { pathname, hash } = useLocation()

  useEffect(() => {
    if (!service.id) {
      return
    }

    switch (hash) {
      case '#__disconnect':
        showNumbersDisconnectModal({ serviceId: service.id })
        break
      case '#__trouble_ticket':
        showCasesCreateTroubleTicket({ serviceId: service.id })
        break
      case '#__usage_plan':
        showNumbersChangeUsagePlanModal({ serviceId: service.id })
        break
      case '#__call_plan':
        showNumbersCallPlanModal({ serviceId: service.id })
        break
    }
  }, [service])

  const {
    isAdmin,
    linkedService,
    children,
    showNumbersEnableFlowsModal,
    showFlowCreateModal,
    linkedCallFlow,
    linkedSmsFlow,
  } = props

  const inactive = service.billingStatus !== 'Active'
  const inactiveMessage = inactive ? 'This service is not active' : ''

  if (!service || !service.number) {
    return null
  }

  const isFlowEnabled =
    (service.usagePlan && service.usagePlan.startsWith('Flows-')) || service.twilioFlowServiceId

  const isVirtualMobileService = service.serviceType === 'Virtual Mobile App'

  const qrCodeUsername = service.username || 'unknown'
  const qrCodePassword = service.password || '******'
  const qrCodeValue = `csc:${encodeURIComponent(qrCodeUsername)}:${encodeURIComponent(
    qrCodePassword
  )}@cloudsip`

  // guess title and url from location.pathname
  // probably it's better to have separate pages for each service type
  const url = pathname.match(new RegExp('/dashboard/([^/]+)'))[1]
  const title = url === 'hosted-voice' ? 'Hosted Voice' : 'Numbers'

  return (
    <Page layout={{ header: 'app' }} title={title}>
      <PageHeader>
        <Crumbs title={title}>
          <Crumb parent to={`/dashboard/${url}`}>
            Services
          </Crumb>
          <Crumb active={!pathname.includes('/calls')} to={`/dashboard/${url}/${service.id}`}>
            {service.number}
          </Crumb>
          <Crumb active={pathname.includes('/calls')} to={`/dashboard/${url}/${service.id}/calls`}>
            Call History
          </Crumb>
        </Crumbs>
      </PageHeader>
      <div className={s.formSection}>
        <Container>
          {children || (
            <div>
              <CallsTrendsVolumeContainer numberId={service.id} inactiveMessage={inactiveMessage} />

              <Row className={cn(sLay['layout-xs-column'], sLay['layout-gt-xs-row'], 'md')}>
                <Col className={cn('md', sLay['flex-gt-xs-60'])}>
                  {/* ******** Reference Edit ******** */}
                  <InfoCard className={sMarg.mb30to15}>
                    <NumbersEdit service={service} />
                  </InfoCard>

                  {/* ******** QR Code ******** */}
                  {isVirtualMobileService ? (
                    <InfoCard
                      title="Virtual Mobile Service Details"
                      className={sMarg.mb30to15}
                      overlayText={inactiveMessage}
                    >
                      <div className={cn(sText.center, sMarg.mb30)}>
                        {inactive ? 'Once your service will be activated you ' : 'You '}
                        can log in to this service using the "Acrobits" "Cloud Softphone" app.
                        {inactive ? '' : ' Download the app and scan the QR code below.'}
                      </div>

                      <div
                        className={cn(
                          sLay['layout-row'],
                          sLay['layout-align-start-center'],
                          sMarg.mb30
                        )}
                      >
                        <QrCode value={qrCodeValue} />

                        <div style={{ marginLeft: '20px' }}>
                          <div>Username: {qrCodeUsername}@cloudsip</div>
                          <div>Password: {qrCodePassword}</div>
                        </div>
                      </div>

                      <div className={s.appStoreButtons}>
                        <a
                          href="https://play.google.com/store/apps/details?id=cz.acrobits.softphone.cloudphone&hl=en_AU&gl=US"
                          target="_blank"
                        >
                          <SVG src="/static/images/app-store/google-play3.svg" className={s.svg} />
                        </a>

                        <a
                          href="https://apps.apple.com/au/app/cloud-softphone/id567475545"
                          target="_blank"
                        >
                          <SVG src="/static/images/app-store/app-store3.svg" className={s.svg} />
                        </a>
                      </div>
                    </InfoCard>
                  ) : (
                    /* ******** Call Flows ******** */
                    <InfoCard title="Call Flows" className={sMarg.mb30to15}>
                      {isFlowEnabled ? (
                        <div>
                          <div className={sText.descrip3} style={{ marginBottom: '20px' }}>
                            Call flows allow you to control what happens when people call your
                            number. Set up forwarding rules, IVR and more.
                          </div>
                          <SimpleTable style={{ marginBottom: '30px' }}>
                            {linkedCallFlow ? (
                              <div>
                                <IconPhone size={20} style={{ marginRight: '10px' }} />
                                <div className={sLay.flex}>
                                  {linkedCallFlow.name || 'Unnamed flow'}
                                </div>
                                <div>
                                  <Button
                                    size="xs"
                                    style={{ color: 'white' }}
                                    color="tertiary"
                                    to={`/dashboard/flows/${linkedCallFlow.id}`}
                                  >
                                    Edit
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className={sLay.flex}>No Linked Call Flow</div>
                                <div>
                                  <Button
                                    size="xs"
                                    style={{ color: 'white' }}
                                    onClick={() =>
                                      showFlowCreateModal({
                                        showType: 'call',
                                        service,
                                      })
                                    }
                                    // title={ !service.twilioCapabilities.voice && 'This is not a voice capable number' }
                                    // disabled={ !service.twilioCapabilities.voice }
                                    color="tertiary"
                                  >
                                    Create Call Flow
                                  </Button>
                                </div>
                              </div>
                            )}
                          </SimpleTable>
                          <div className={sText.descrip3} style={{ marginBottom: '20px' }}>
                            SMS flows allow you to control what happens when people SMS your number.
                            Set up auto responses, emails, analytics and more.
                          </div>
                          <SimpleTable style={{ marginBottom: '30px' }}>
                            {linkedSmsFlow ? (
                              <div>
                                <IconCellMessage size={20} style={{ marginRight: '10px' }} />
                                <div className={sLay.flex}>
                                  {linkedSmsFlow.name || 'Unnamed flow'}
                                </div>
                                <div>
                                  <Button
                                    size="xs"
                                    style={{ color: 'white' }}
                                    color="tertiary"
                                    to={`/dashboard/flows/${linkedSmsFlow.id}`}
                                  >
                                    Edit
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className={sLay.flex}>No Linked SMS Flow</div>
                                <div>
                                  <Button
                                    size="xs"
                                    style={{ color: 'white' }}
                                    onClick={() =>
                                      showFlowCreateModal({
                                        showType: 'sms',
                                        service,
                                      })
                                    }
                                    color="tertiary"
                                    title={
                                      !service.twilioCapabilities.SMS &&
                                      'This is not an SMS capable number'
                                    }
                                    disabled={!service.twilioCapabilities.SMS}
                                  >
                                    Create SMS Flow
                                  </Button>
                                </div>
                              </div>
                            )}
                          </SimpleTable>
                          {/* { unlinkedFlows.length > 0 && (
                            <div>
                              <div className={ sText.descrip2 } style={ { marginBottom: '10px' } }>Unlinked Flows:</div>
                              <div className={ sText.descrip4 } style={ { marginBottom: '10px' } }>
                                Only 1 flow can be linked to a number at any time. Edit one of these flows and click "Publish" to link it.
                              </div>
                              <SimpleTable>
                                { unlinkedFlows.map(flow => (
                                  <div key={ flow.id }>
                                    <IconLinkOff size={ 20 } style={ { marginRight: '10px' } } />
                                    <div className={ sLay.flex }>{ flow.name }</div>
                                    <div>
                                      <Button
                                        size="xs"
                                        style={ { color: 'white' } }
                                        color="tertiary"
                                        to={ `/dashboard/flows/${flow.id}` }
                                      >
                                    Edit
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </SimpleTable>
                            </div>
                          )} */}
                        </div>
                      ) : (
                        <div>
                          <div className={sText.descrip3}>
                            Call flows are NOT enabled for this number. Call flows can be added for
                            an additional $10 per month. Note, this feature is in Beta. Please
                            contact us before using it.
                          </div>
                          <div className={sMarg.mt30} style={{ position: 'relative' }}>
                            <Button
                              disabled={inactive}
                              title={inactiveMessage}
                              color="tertiary"
                              onClick={() =>
                                showNumbersEnableFlowsModal({
                                  serviceId: service.id,
                                  phoneNumber: service.number,
                                  ban: service.ban,
                                })
                              }
                            >
                              Enable flows
                            </Button>
                            {inactiveMessage && <Tooltip>{inactiveMessage}</Tooltip>}
                          </div>
                        </div>
                      )}
                    </InfoCard>
                  )}

                  {/* ******** Billing Details ******** */}
                  <InfoCard title="Billing Details" className={sMarg.mb30to15}>
                    <SimpleTable>
                      <div>
                        <div>Billing Account:</div>
                        <div>
                          <Link
                            to={`/dashboard/billing/accounts/${service.ban}`}
                            onlyActiveOnIndex={false}
                          >
                            {service.ban}
                          </Link>
                        </div>
                      </div>
                      <div>
                        <div>Billing Status:</div>
                        <div>{service.billingStatus}</div>
                      </div>
                      {service.billingStartDate && (
                        <div>
                          <div>Billing Start Date:</div>
                          <div>{moment(service.billingStartDate).format('DD MMM YYYY')}</div>
                        </div>
                      )}
                      {service.billingEndDate && (
                        <div>
                          <div>Billing End Date:</div>
                          <div>{moment(service.billingEndDate).format('DD MMM YYYY')}</div>
                        </div>
                      )}
                      <div>
                        <div>Monthly Charge:</div>
                        <div>${service.monthlyCharge}</div>
                      </div>
                    </SimpleTable>
                  </InfoCard>

                  {/* ******** Number Details ******** */}
                  <InfoCard title="Number Details" className={sMarg.mb30to15}>
                    <SimpleTable>
                      <div>
                        <div>Number:</div>
                        <div>{service.number}</div>
                      </div>
                      {linkedService.number && (
                        <div>
                          <div>Local Alias:</div>
                          <div>{linkedService.number}</div>
                        </div>
                      )}
                      {service.currentStatus && (
                        <div>
                          <div>Curent Status: </div>
                          <div>{service.currentStatus}</div>
                        </div>
                      )}
                      {service.contractEnd && (
                        <div>
                          <div>Contract End Date:</div>
                          <div>{moment(service.contractEnd).format('DD MMM YYYY')}</div>
                        </div>
                      )}
                      {service.usagePlan && (
                        <div>
                          <div>Plan:</div>
                          <div>{service.usagePlan}</div>
                        </div>
                      )}
                    </SimpleTable>
                  </InfoCard>
                </Col>
                <Col className={cn(sLay['flex-gt-xs-40'], 'md')}>
                  <StickyChannel>
                    <div className={sLay['layout-column']} style={{ position: 'relative' }}>
                      {inactiveMessage && <Tooltip>{inactiveMessage}</Tooltip>}
                      {isVirtualMobileService ? (
                        <>
                          <Button
                            onClick={() => showCasesCreateTroubleTicket({ serviceId: service.id })}
                            className={sMarg.mb15}
                            disabled={inactive}
                            color="secondary"
                            size="sm"
                            rect
                          >
                            Submit a Trouble Ticket
                          </Button>

                          <Button
                            onClick={() =>
                              showNumbersDisconnectModal({
                                serviceId: service.id,
                              })
                            }
                            className={sMarg.mb15}
                            color="warn"
                            size="sm"
                            rect
                            disabled={inactive}
                          >
                            Disconnect
                          </Button>
                        </>
                      ) : (
                        <>
                          {!isFlowEnabled && (
                            <Button
                              onClick={() => showNumbersCallPlanModal({ serviceId: service.id })}
                              className={sMarg.mb15}
                              color="tertiary"
                              size="sm"
                              rect
                              disabled={inactive}
                            >
                              Edit Call Forwarding
                            </Button>
                          )}
                          <Button
                            onClick={() => showCasesCreateTroubleTicket({ serviceId: service.id })}
                            className={sMarg.mb15}
                            disabled={inactive}
                            color="secondary"
                            size="sm"
                            rect
                          >
                            Submit a Trouble Ticket
                          </Button>

                          <Button
                            onClick={() =>
                              showNumbersDisconnectModal({
                                serviceId: service.id,
                                number: service.number,
                              })
                            }
                            className={sMarg.mb15}
                            color="warn"
                            size="sm"
                            rect
                            disabled={inactive}
                          >
                            Disconnect
                          </Button>

                          {isAdmin ? (
                            <Button
                              onClick={() =>
                                showNumbersChangeUsagePlanModal({
                                  serviceId: service.id,
                                  // number: service.number,
                                })
                              }
                              className={sMarg.mb15}
                              color="primary"
                              size="sm"
                              rect
                            >
                              Admin: Change Usage Plan
                            </Button>
                          ) : null}
                        </>
                      )}
                    </div>
                    <CasesTimeline serviceId={service.id} />
                  </StickyChannel>
                </Col>
              </Row>
            </div>
          )}
        </Container>
      </div>
    </Page>
  )
}



// WEBPACK FOOTER //
// ./src/pages/NumbersEdit/NumbersEdit.jsx